// V-shape column resolution.
//
// The single new data primitive for the s-expr dashboard renderer. A
// `DashboardSpec` tile carries unresolved `ColumnRef`s (V-shape, no
// I/O). `useResolvedColumns` turns a tile's refs into their column
// data on demand: it resolves each ref to an engine `Dataset` via
// `resolveColumnRef` (cross-turn binding lookup against the in-scope
// recipes), materialises it, and returns the column-oriented cell
// arrays.
//
// One `useSuspenseQuery` per call (array in / array out) so the hook
// count is stable regardless of how many series/columns a tile has
// (React rules-of-hooks). Identical ref sets across tiles WITH the
// same binding scope share the TanStack cache entry — this is the
// spec's "coalesce materialisation across tiles".

import { useSuspenseQuery } from '@tanstack/react-query';
import { useDeferredValue } from 'react';

import { NO_POS } from '#core/lib/expr/ast.ts';
import { projectColumnRef } from '#core/lib/expr/column-ref.ts';
import { useBindingResolver } from '#core/lib/expr/session/BindingScopeContext.ts';
import { EXPR_FAIL_FAST, exprQueryKey } from '#core/lib/expr/session/exprQueryKey.ts';
import { useSession } from '#core/lib/expr/session/SessionContext.ts';
import { useMaterialize } from '#core/lib/expr/session/useMaterialize.ts';
import type { Cell, ColumnRef } from '#core/lib/expr/value.ts';

/**
 * Resolve + materialise a list of `ColumnRef`s to their cell arrays,
 * preserving order. Suspends until every ref is materialised; the
 * enclosing tile's `<Suspense>` shows its skeleton meanwhile.
 */
export function useResolvedColumns(refs: readonly ColumnRef[]): readonly (readonly Cell[])[] {
  const session = useSession();
  const bindings = useBindingResolver();
  const materialize = useMaterialize();
  // Stable cache key: column + path identifies the projection; ref
  // index distinguishes same-column refs in different tile slots.
  // `bindings.recipes` is the position-scoped producer snapshot, so a
  // different transcript position (or branch) re-derives while an
  // unchanged scope reuses the cache (keeps array identity stable across
  // streaming re-renders).
  const refKey = refs.map((r, i) => [i, r.column, r.path] as const);
  const { data } = useSuspenseQuery({
    queryKey: exprQueryKey('dashboard-spec-columns', session, bindings, refKey),
    queryFn: async (): Promise<readonly (readonly Cell[])[]> =>
      Promise.all(
        refs.map(async (ref) => {
          // SSOT: resolve → materialise-if-lazy → per-cell `ref.path`
          // walk. This applies the struct sub-path so `(col ds s a)`
          // yields the leaf scalar cells, not the raw struct objects.
          const { cells } = await projectColumnRef(ref, NO_POS, materialize);
          return cells;
        }),
      ),
    ...EXPR_FAIL_FAST,
  });
  return useDeferredValue(data);
}
